import Dexie from "dexie";
import { ContextOrderBasketType } from "./hooks";
import type { ISODateString } from "./types/isoDate";
import type { CountryType } from "@sellar-io/common";

interface RetailerOrderBasketType extends ContextOrderBasketType {
  RetailerId: number | null;
  _fromOrderFlowToken?: boolean; // Marker for orderFlowToken flow orders
}

export interface SDBasketType {
  id?: number;
  uuid: string;
  retailerId: number;
  deliveryDate?: ISODateString | null;
  orderCutoffDate?: string | null;
  createdAt: string;

  // Checkout details
  deliveryAddress?: {
    name: string;
    street: string;
    city: string;
    region: string;
    postalCode: string;
    country: CountryType;
  } | null;
  phoneNumber?: string | null;
  deliveryInstructions?: string | null;
  ordersComment?: string | null;
  hasNoDraughtDutyRelief?: boolean;
  deliveryPreference?: "consolidated" | "split" | null;
  contactId?: number | null;
  timeSlotPreference?: "AM" | "PM" | "flexible" | null;

  // Payment details (Phase 3d)
  paidSupplierIds?: number[] | null;
  paidOrderIds?: number[] | null;
  failedSupplierIds?: number[] | null;
  failedOrderIds?: number[] | null;
  pendingSupplierIds?: number[] | null;
  pendingOrderIds?: number[] | null;
}

class SellarLocalStorageDb extends Dexie {
  public draftOrders: Dexie.Table<RetailerOrderBasketType>;

  public constructor() {
    super("sellarLocalStorageDb");
    this.version(4).stores({
      draftOrders:
        "[SupplierId+RetailerId+initiator], [RetailerId+initiator], [SupplierId+initiator], SupplierId, RetailerId, initiator, sdBasketId",
      sdBaskets: "++id, retailerId, deliveryDate, createdAt",
    });
    this.version(5)
      .stores({
        draftOrders:
          "[SupplierId+RetailerId+initiator], [RetailerId+initiator], [SupplierId+initiator], SupplierId, RetailerId, initiator, sdBasketId",
        sdBaskets: "++id, &uuid, retailerId, deliveryDate, createdAt",
      })
      .upgrade(async (tx) => {
        // Backfill uuid on any existing sdBaskets rows that were created before v5
        const baskets = await tx.table("sdBaskets").toArray();
        await Promise.all(
          baskets
            .filter((b) => !b.uuid)
            .map((b) =>
              tx.table("sdBaskets").update(b.id, { uuid: crypto.randomUUID() }),
            ),
        );
      });
    this.version(6).stores({
      draftOrders:
        "[SupplierId+RetailerId+initiator], [RetailerId+initiator], [SupplierId+initiator], SupplierId, RetailerId, initiator, sdBasketId",
      sdBaskets:
        "++id, &uuid, retailerId, deliveryDate, createdAt, deliveryPreference, contactPreference, timeSlotPreference",
    });
    this.version(7).stores({
      draftOrders:
        "[SupplierId+RetailerId+initiator], [RetailerId+initiator], [SupplierId+initiator], SupplierId, RetailerId, initiator, sdBasketId",
      sdBaskets:
        "++id, &uuid, retailerId, deliveryDate, createdAt, deliveryPreference, contactPreference, timeSlotPreference, paymentMethods, paidSupplierIds",
    });
    this.version(8).stores({
      draftOrders:
        "[SupplierId+RetailerId+initiator], [RetailerId+initiator], [SupplierId+initiator], SupplierId, RetailerId, initiator, sdBasketId",
      sdBaskets:
        "++id, &uuid, retailerId, deliveryDate, createdAt, deliveryPreference, contactPreference, timeSlotPreference, paymentMethods, paidSupplierIds, paidOrderIds, failedSupplierIds, failedOrderIds",
    });
    this.version(9)
      .stores({
        draftOrders:
          "[SupplierId+RetailerId+initiator], [RetailerId+initiator], [SupplierId+initiator], SupplierId, RetailerId, initiator, sdBasketId",
        sdBaskets:
          "++id, &uuid, retailerId, deliveryDate, createdAt, deliveryPreference, contactPreference, timeSlotPreference, paymentMethods, paidSupplierIds, paidOrderIds, failedSupplierIds, failedOrderIds, pendingSupplierIds, pendingOrderIds",
      })
      .upgrade(async (tx) => {
        // Treat previously-failed orders as paid-with-pending-payment so that
        // order creation is the signal of success and progress is always allowed.
        const baskets = await tx.table("sdBaskets").toArray();
        await Promise.all(
          baskets.map((basket) => {
            const paidSupplierIds = basket.paidSupplierIds ?? [];
            const paidOrderIds = basket.paidOrderIds ?? [];
            const failedSupplierIds = basket.failedSupplierIds ?? [];
            const failedOrderIds = basket.failedOrderIds ?? [];
            const pendingSupplierIds = basket.pendingSupplierIds ?? [];
            const pendingOrderIds = basket.pendingOrderIds ?? [];
            return tx.table("sdBaskets").update(basket.id, {
              paidSupplierIds: Array.from(
                new Set([...paidSupplierIds, ...failedSupplierIds]),
              ),
              paidOrderIds: Array.from(
                new Set([...paidOrderIds, ...failedOrderIds]),
              ),
              pendingSupplierIds: Array.from(
                new Set([...pendingSupplierIds, ...failedSupplierIds]),
              ),
              pendingOrderIds: Array.from(
                new Set([...pendingOrderIds, ...failedOrderIds]),
              ),
              failedSupplierIds: [],
              failedOrderIds: [],
            });
          }),
        );
      });
    this.version(10).stores({
      draftOrders:
        "[SupplierId+RetailerId+initiator], [RetailerId+initiator], [SupplierId+initiator], SupplierId, RetailerId, initiator, sdBasketId, expectedDeliveryDate",
      sdBaskets:
        "++id, &uuid, retailerId, deliveryDate, orderCutoffDate, createdAt, deliveryPreference, contactPreference, timeSlotPreference, paymentMethods, paidSupplierIds, paidOrderIds, failedSupplierIds, failedOrderIds, pendingSupplierIds, pendingOrderIds",
    });
    this.version(11)
      .stores({
        draftOrders:
          "[SupplierId+RetailerId+initiator], [RetailerId+initiator], [SupplierId+initiator], SupplierId, RetailerId, initiator",
        sdBaskets: null,
      })
      .upgrade(async (tx) => {
        await tx
          .table("draftOrders")
          .filter((order) => order.fulfillmentType === "sellarDistribution")
          .delete();
      });
    this.draftOrders = this.table("draftOrders");
  }

  // public open() {
  //   if (this.isOpen()) return super.open();
  //
  //   return Dexie.Promise.resolve()
  //     .then(() => Dexie.exists(this.name))
  //     .then((exists) => {
  //       if (!exists) {
  //         // no need to check database version since it doesn't exist
  //         return;
  //       }
  //
  //       // Open separate instance of dexie to get current database version
  //       return new Dexie(this.name).open().then(async (db) => {
  //         if (db.verno >= SellarLocalStorageDb.targetVersion) {
  //           // database up to date (or newer)
  //           return db.close();
  //         }
  //
  //         console.log(
  //           `Database schema out of date, resetting all data. (currentVersion: ${db.verno}, expectedVersion: ${SellarLocalStorageDb.targetVersion})`,
  //         );
  //         await db.delete();
  //
  //         // ensure the delete was successful
  //         const exists = await Dexie.exists(this.name);
  //         if (exists) {
  //           throw new Error("Failed to remove mock backend database.");
  //         }
  //       });
  //     })
  //     .then(() => super.open());
  // }
}

const db = new SellarLocalStorageDb();

export default db;
